import type { View } from '../../App';
import PerspectiveCard from './PerspectiveCard';

interface HeroProps {
  onViewChange: (view: View) => void;
}

function RadarIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="currentColor" strokeWidth="1.5">
      {/* Dish base */}
      <path d="M18 32V24" />
      <path d="M12 32h12" strokeLinecap="round" />
      {/* Dish curve */}
      <path d="M6 18c0-6.627 5.373-12 12-12s12 5.373 12 12" strokeLinecap="round" />
      {/* Inner ring */}
      <path d="M10 18a8 8 0 0 1 16 0" strokeLinecap="round" />
      {/* Center dot */}
      <circle cx="18" cy="18" r="2" fill="currentColor" stroke="none" />
      {/* Sweep line */}
      <line x1="18" y1="18" x2="24" y2="10" strokeLinecap="round" />
      {/* Signal arcs */}
      <path d="M26 8a14 14 0 0 1 2.5 4" strokeLinecap="round" strokeDasharray="2 2" />
    </svg>
  );
}

function BroadcastIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="currentColor" strokeWidth="1.5">
      {/* Center figure */}
      <circle cx="18" cy="18" r="3" fill="currentColor" stroke="none" />
      {/* Signal rings */}
      <path d="M12 12a8.49 8.49 0 0 1 12 0" strokeLinecap="round" />
      <path d="M24 24a8.49 8.49 0 0 1-12 0" strokeLinecap="round" />
      <path d="M8 8a14.14 14.14 0 0 1 20 0" strokeLinecap="round" />
      <path d="M28 28a14.14 14.14 0 0 1-20 0" strokeLinecap="round" />
      {/* Vertical signal */}
      <line x1="18" y1="4" x2="18" y2="10" strokeLinecap="round" strokeDasharray="2 2" />
      <line x1="18" y1="26" x2="18" y2="32" strokeLinecap="round" strokeDasharray="2 2" />
    </svg>
  );
}

export default function Hero({ onViewChange }: HeroProps) {
  return (
    <section className="min-h-[calc(100vh-3.5rem)] flex flex-col items-center justify-center px-6 py-16">
      <div className="max-w-4xl mx-auto text-center mb-12">
        {/* Headline */}
        <h1 className="text-5xl sm:text-6xl font-bold mb-6 bg-gradient-to-r from-ti-blue to-ti-purple bg-clip-text text-transparent leading-tight">
          The Front Door is Broken
        </h1>

        {/* Subheadline */}
        <p className="text-xl text-ti-text-muted max-w-2xl mx-auto leading-relaxed">
          What if companies recruited like intelligence analysts?
          <br />
          What if candidates were machine-discoverable?
        </p>
      </div>

      {/* Perspective Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto w-full mb-16">
        <PerspectiveCard
          title="I'm Hiring"
          description="See how intelligence-driven recruiting finds what ATS misses"
          icon={<RadarIcon />}
          onClick={() => onViewChange('company')}
          accentColor="#3b82f6"
        />
        <PerspectiveCard
          title="I'm a Candidate"
          description="See what a machine-readable professional profile looks like"
          icon={<BroadcastIcon />}
          onClick={() => onViewChange('candidate')}
          accentColor="#8b5cf6"
        />
      </div>

      {/* Thesis */}
      <div className="max-w-2xl mx-auto text-center">
        <p className="text-ti-text-muted leading-relaxed mb-6">
          The recruiting industry routes talent through an application-first bottleneck
          that discards signal, rewards keyword gaming, and leaves both sides guessing.
          TalentIntel demonstrates a different model: one where companies field intelligence
          agents and candidates broadcast structured, verifiable profiles -- turning hiring
          from a filtering problem into a matching one.
        </p>

        {/* Article Link */}
        <div className="inline-flex items-center gap-2 text-ti-blue hover:text-ti-cyan transition-colors">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15,3 21,3 21,9" />
            <line x1="10" x2="21" y1="14" y2="3" />
          </svg>
          <a
            href="https://www.linkedin.com/pulse/front-door-broken-why-future-recruiting-looks-more-like-ted-lango-znwhe"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium"
          >
            Read the Full Article
          </a>
        </div>
      </div>
    </section>
  );
}
